export type RGB = { r: number; g: number; b: number }
export type HSV = { h: number; s: number; v: number }

const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max)

export function hexToRgb(hex: string): RGB {
  let s = hex.trim().replace(/^#/, '')
  if (s.length === 3) {
    s = s.split('').map((c) => c + c).join('')
  }
  const num = parseInt(s, 16)
  const r = (num >> 16) & 255
  const g = (num >> 8) & 255
  const b = num & 255
  return { r, g, b }
}

export function rgbToHex({ r, g, b }: RGB): string {
  const toHex = (n: number) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase()
}

export function rgbToHsv({ r, g, b }: RGB): HSV {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const d = max - min
  let h = 0
  if (d !== 0) {
    switch (max) {
      case rn:
        h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60
        break
      case gn:
        h = ((bn - rn) / d + 2) * 60
        break
      case bn:
        h = ((rn - gn) / d + 4) * 60
        break
    }
  }
  const s = max === 0 ? 0 : d / max
  const v = max
  return { h, s: s * 100, v: v * 100 }
}

export function hsvToRgb({ h, s, v }: HSV): RGB {
  const S = clamp(s, 0, 100) / 100
  const V = clamp(v, 0, 100) / 100
  const C = V * S
  const X = C * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = V - C

  let r1 = 0, g1 = 0, b1 = 0
  if (h >= 0 && h < 60) { r1 = C; g1 = X; b1 = 0 }
  else if (h < 120) { r1 = X; g1 = C; b1 = 0 }
  else if (h < 180) { r1 = 0; g1 = C; b1 = X }
  else if (h < 240) { r1 = 0; g1 = X; b1 = C }
  else if (h < 300) { r1 = X; g1 = 0; b1 = C }
  else { r1 = C; g1 = 0; b1 = X }

  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  }
}

export function hexToHsv(hex: string): HSV {
  return rgbToHsv(hexToRgb(hex))
}

export function hsvToHex(hsv: HSV): string {
  return rgbToHex(hsvToRgb(hsv))
}

export function hueToHslString(h: number): string {
  const hh = ((h % 360) + 360) % 360
  return `hsl(${hh}, 100%, 50%)`
}


const FULL_HEX = /^#?[0-9A-Fa-f]{6}$/

const channelToLinear = (channel: number) => {
  const normalized = channel / 255
  return normalized <= 0.03928
    ? normalized / 12.92
    : Math.pow((normalized + 0.055) / 1.055, 2.4)
}

/** WCAG relative luminance of a 6-digit hex colour, or null if it is not one. */
export function relativeLuminance(hex: string): number | null {
  const value = hex.trim()
  if (!FULL_HEX.test(value)) return null
  const { r, g, b } = hexToRgb(value)
  return 0.2126 * channelToLinear(r) + 0.7152 * channelToLinear(g) + 0.0722 * channelToLinear(b)
}

/** WCAG contrast ratio (1–21) of two 6-digit hex colours, or null if either is not one. */
export function contrastRatio(a: string, b: string): number | null {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  if (la === null || lb === null) return null
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/** Adds `amount` to every RGB channel (negative darkens), clamped to 0–255. */
export function shiftHex(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex)
  return rgbToHex({ r: r + amount, g: g + amount, b: b + amount }).toLowerCase()
}
